<template lang="pug">
  span(v-if="value")
    .form-group
      label {{ label }}
      date-range-picker(ref="pickerRef", :localeData="datePickerOptions.locale", :ranges="datePickerOptions.ranges",
        :auto-apply="true", :date-range="value.dateRange", @update="(v) => onInput('dateRange', v)", :time-picker="!singleDateOnly || showTime", :time-picker24-hour="!singleDateOnly || showTime",
        :time-picker-increment="1", opens="right", :date-format="dateFormat",
        :single-date-picker="singleDateOnly", :min-date="minDate", :max-date="maxDate", :class="{'w-100': fullWidth}")
        template(#ranges="{ startDate, endDate, ranges }")
          .ranges
            ul(v-if="ranges")
              li(v-for="(rangeVal, key) in ranges", :key="key",
                @click="$refs.pickerRef.clickRange(rangeVal)",
                :class="{ active: isRangeActive(startDate, endDate, rangeVal) }") {{ key }}
      span(v-if='limitThreeMonths')
        i.la.la-info-circle.la-lg.ml-2(v-b-tooltip.hover.right, title="Dates range limited to last 3 months due to selected dimensions.")
    .form-group(v-if="!value.hideTZ", :class="{'mobile-hidden': minimized && !value.timezone}")
      label Timezone
      select.form-control(:value="value.timezone", @input="(e) => onInput('timezone', e.target.value)")
        option(v-for="t in timeZoneOptions", :value="t.v") {{ t.t }}
</template>
<script>
import Vue from 'vue';
import moment from 'moment';
import { copy } from '../../lib/objects';

export default {
  name: 'OvDateTimePicker',
  props: {
    label: {
      type: String,
      required: false,
      default() {
        return 'Dates';
      },
    },
    value: {
      type: Object,
      required: true,
    },
    limitThreeMonths: {
      type: Boolean,
      required: false,
      default() {
        return false;
      },
    },
    minDate: {
      type: Date,
      required: false,
      default() {
        return null;
      },
    },
    maxDate: {
      type: Date,
      required: false,
      default() {
        return null;
      },
    },
    minimized: {
      type: Boolean,
      required: false,
      default() {
        return false;
      },
    },
    singleDateOnly: {
      type: Boolean,
      required: false,
      default() {
        return false;
      },
    },
    showTime: {
      type: Boolean,
      required: false,
      default() {
        return false;
      },
    },
    /**
    * Optional ordered list of preset ranges to display.
    * Only the ranges in this list will be shown, in the specified order.
    * If omitted, a default set of ranges will be shown.
    */
    visibleRanges: {
      type: Array,
      required: false,
      default: () => [
        'This Hour',
        'Last Hour',
        'Today',
        'Yesterday',
        'Last 7 Days',
        'Last 30 Days',
        'This Month',
        'Last Month',
        'Last 1 Year',
      ],
    },
    fullWidth: {
        type: Boolean,
        required: false,
        default() {
          return false;
        },
      },
  },
  watch: {
    visibleRanges() {
      this.updateRanges();
    },
    limitThreeMonths() {
      this.updateRanges();
    },
    minDate() {
      this.updateRanges();
    },
  },
  data() {
    return {
      timeZoneOptions: Vue.ovData.general.timezoneOptions,
      datePickerOptions: {
        timePicker: true,
        timePicker24Hour: true,
        locale: {
          // vue2-daterange-picker uses dateFormat tokens (dd/mm/yyyy, HH:MM), not moment.js.
          format: 'dd/mm/yyyy HH:MM',
        },
        alwaysShowCalendars: true,
        autoApply: true,
        ranges: this.$props.singleDateOnly ? false : {},
      },
    };
  },
  methods: {
    onInput(key, value) {
      const newValue = copy(this.value);
      newValue[key] = value;
      this.$emit('input', newValue);
      this.$emit('change', newValue);
    },
    isRangeActive(startDate, endDate, range) {
      if (!startDate || !endDate || !range || !range[0] || !range[1]) return false;
      const s = moment(startDate).startOf('minute');
      const e = moment(endDate).startOf('minute');
      const r0 = moment(range[0]).startOf('minute');
      const r1 = moment(range[1]).startOf('minute');
      return s.isSame(r0) && e.isSame(r1);
    },
    dateFormat(classes, date) {
      if (this.limitThreeMonths && !classes.disabled) {
        const threeMontshBefore = moment().subtract(3, 'months');
        classes.disabled = date < threeMontshBefore;
      }
      return classes;
    },
    updateRanges() {
      if (this.$props.singleDateOnly) return false;

      let tz = this.value ? this.value.timezone || 0 : 0;
      let localOffset = new Date().getTimezoneOffset() / 60;
      let tzGap = -localOffset - tz;

      const allRanges = {
        'This Hour': [
          moment(
            moment().subtract(tzGap, 'hours').format('DD/MM/YYYY HH') + ':00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').format('DD/MM/YYYY HH') + ':59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        'Last Hour': [
          moment(
            moment().subtract(tzGap, 'hours').subtract(1, 'hours').format('DD/MM/YYYY HH') + ':00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').subtract(1, 'hours').format('DD/MM/YYYY HH') + ':59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        Today: [
          moment(
            moment().subtract(tzGap, 'hours').format('DD/MM/YYYY') + ' 00:00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').format('DD/MM/YYYY') + ' 23:59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        Yesterday: [
          moment(
            moment().subtract(tzGap, 'hours').subtract(1, 'days').format('DD/MM/YYYY') + ' 00:00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').subtract(1, 'days').format('DD/MM/YYYY') + ' 23:59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        'Last 7 Days': [
          moment(
            moment().subtract(tzGap, 'hours').subtract(6, 'days').format('DD/MM/YYYY') + ' 00:00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').format('DD/MM/YYYY') + ' 23:59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        'Last 30 Days': [
          moment(
            moment().subtract(tzGap, 'hours').subtract(29, 'days').format('DD/MM/YYYY') + ' 00:00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').format('DD/MM/YYYY') + ' 23:59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        'This Month': [
          moment(
            moment().subtract(tzGap, 'hours').startOf('month').format('DD/MM/YYYY') + ' 00:00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').endOf('month').format('DD/MM/YYYY') + ' 23:59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        'Last Month': [
          moment(
            moment()
              .subtract(tzGap, 'hours')
              .subtract(1, 'month')
              .startOf('month')
              .format('DD/MM/YYYY') + ' 00:00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment()
              .subtract(tzGap, 'hours')
              .subtract(1, 'month')
              .endOf('month')
              .format('DD/MM/YYYY') + ' 23:59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        'MTD': [
          moment(
            moment().subtract(tzGap, 'hours').startOf('month').format('DD/MM/YYYY') + ' 00:00',
            'DD/MM/YYYY HH:mm',
          ),
          moment(
            moment().subtract(tzGap, 'hours').format('DD/MM/YYYY') + ' 23:59',
            'DD/MM/YYYY HH:mm',
          ),
        ],
        'Last 1 Year': this.lastYearRange(tzGap),
      };

      this.datePickerOptions.ranges = this.visibleRanges.reduce((acc, key) => {
        if (!allRanges[key]) return acc;
        const dates = allRanges[key].map((d) => (d instanceof Date ? d : d.toDate()));
        if (this.rangeWithinLimits(key, dates[0])) {
          acc[key] = dates;
        }
        return acc;
      }, {});
    },
    /**
     * Rolling year ending today, same inclusive rule as Last 7 / Last 30 Days:
     * start is one calendar year ago plus one day (00:00) through today (23:59).
     * Calendar subtraction keeps leap days (Feb 29 clamps to Feb 28).
     */
    lastYearRange(tzGap) {
      const shifted = moment().subtract(tzGap, 'hours');
      const start = shifted.clone().subtract(1, 'year').add(1, 'day');
      return [
        moment(start.format('DD/MM/YYYY') + ' 00:00', 'DD/MM/YYYY HH:mm'),
        moment(shifted.format('DD/MM/YYYY') + ' 23:59', 'DD/MM/YYYY HH:mm'),
      ];
    },
    rangeWithinLimits(key, start) {
      if (key !== 'Last 1 Year') return true;
      if (this.limitThreeMonths && moment(start).isBefore(moment().subtract(3, 'months'), 'day')) {
        return false;
      }
      if (this.minDate && moment(start).isBefore(this.minDate, 'day')) {
        return false;
      }
      return true;
    },
    setRangeByKey(rangeKey) {
      const selectedRange = this.datePickerOptions.ranges[rangeKey];
      if (selectedRange) {
        this.$nextTick(() => {
          this.onInput('dateRange', {
            startDate: selectedRange[0],
            endDate: selectedRange[1],
          });
        });
      }
    },
  },
  mounted() {
    if (!this.value.dateRange.startDate) {
      this.value.dateRange.startDate = moment(
        moment()
          .subtract(-(new Date().getTimezoneOffset() / 60), 'hours')
          .format('DD/MM/YYYY') + ' 00:00',
        'DD/MM/YYYY HH:mm',
      );
    }
    if (!this.value.dateRange.endDate) {
      this.value.dateRange.endDate = moment(
        moment()
          .subtract(-(new Date().getTimezoneOffset() / 60), 'hours')
          .format('DD/MM/YYYY') + ' 23:59',
        'DD/MM/YYYY HH:mm',
      );
    }
    this.updateRanges();
    let intv = setInterval(() => {
      this.updateRanges();
    }, 60 * 1000);
    this.$once('hook:beforeDestroy', () => {
      clearInterval(intv);
    });
  },
};
</script>
